import moment from "moment";

import { AvatarItem } from "../../../../../components/newUI/avatar";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../../../constants/constants";
import { ContactTagCustomerModel } from "../../../../../factories/contacts/models/contact-tag.model";
import { CustomerModel } from "../../../../../factories/customers/models/customerModel";
import { RoleModel } from "../../../../../factories/role/role.model";
import { AddWorkDto } from "../../../../../factories/works/dtos/add-work.dto";
import { IUser, SelectOptionType } from "../../../../../types/types";
import { AddWorkContactOption, AddWorkFormData } from "../model/addWork.types";

export const uniqueOptionsByValue = <T extends { value?: string }>(
  options: T[],
): T[] =>
  options.reduce<T[]>((acc, option) => {
    if (!option?.value) {
      return acc;
    }

    if (!acc.some((item) => item.value === option.value)) {
      acc.push(option);
    }

    return acc;
  }, []);

const getContactAvatarColor = (isOrganization: boolean) =>
  isOrganization ? "secondary" : "primary";

export const mapLegacyContactToContactOption = (
  contact: SelectOptionType,
): AddWorkContactOption => {
  const isOrganization =
    !!contact.businessContactId ||
    contact.avatarColor === "purple" ||
    contact.type === "purple";

  return {
    value: contact.value,
    label: contact.label,
    caption: isOrganization ? "Organization" : "Individual",
    avatar: {
      id: contact.value,
      label: contact.avatar || getInitialsFromFullName(contact.label),
      fullName: contact.label,
      color: getContactAvatarColor(isOrganization),
      size: "sm",
    },
  };
};

export const mapCustomerToContactOption = (
  customer: CustomerModel,
): AddWorkContactOption => {
  const isOrganization = !!customer.businessContactId;

  return {
    value: customer.customerId,
    label: customer.customerName,
    caption: isOrganization ? "Organization" : "Individual",
    avatar: {
      id: customer.customerId,
      label: getInitialsFromFullName(customer.customerName),
      fullName: customer.customerName,
      color: getContactAvatarColor(isOrganization),
      size: "sm",
    },
  };
};

export const mapTagCustomerToContactOption = (
  customer: ContactTagCustomerModel,
): AddWorkContactOption => {
  const isOrganization = !!customer.businessContactId;

  return {
    value: customer.id,
    label: customer.name,
    caption: isOrganization ? "Organization" : "Individual",
    avatar: {
      id: customer.id,
      label: getInitialsFromFullName(customer.name),
      fullName: customer.name,
      color: getContactAvatarColor(isOrganization),
      size: "sm",
    },
  };
};

export const getSelectedRole = (
  roles: RoleModel[] | undefined,
  roleId: string | null,
): RoleModel | undefined => roles?.find((role) => role.id === roleId);

export const findOptionByValue = (
  options: SelectOptionType[],
  value?: string | null,
): SelectOptionType | null => {
  if (!value) {
    return null;
  }

  return options.find((option) => option.value === value) ?? null;
};

export const buildUserOption = (user: IUser): SelectOptionType => {
  const firstName = user?.userProfile?.firstName || "";
  const lastName = user?.userProfile?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    label: fullName,
    value: user.id,
    avatar: getInitials(firstName, lastName),
    type: "custom",
    avatarCustomColor: user?.userProfile?.color,
  };
};

export const buildDefaultValues = (
  user: IUser | null | undefined,
  defaultContact?: SelectOptionType,
  defaultContacts?: SelectOptionType[],
): AddWorkFormData => {
  const assignedTo: SelectOptionType[] = user?.id
    ? [buildUserOption(user)]
    : [];
  const initialContacts = uniqueOptionsByValue(
    (defaultContacts?.length
      ? defaultContacts
      : defaultContact
      ? [defaultContact]
      : []
    ).map(mapLegacyContactToContactOption),
  );

  return {
    workName: "",
    workType: null,
    subtaskTemplate: null,
    contact: initialContacts,
    role: null,
    budgetedTime: "0",
    assignedTo,
    startDate: moment().toISOString(),
    dueDate: moment().toISOString(),
  };
};

export const getTimeBudget = (budgetedTime: string): number => {
  const timeBudget = Number(budgetedTime);

  return Number.isFinite(timeBudget) ? Math.max(timeBudget, 0) : 0;
};

export const getSelectedAssigneeItems = (
  assignees: SelectOptionType[],
): AvatarItem[] =>
  assignees.map((assignee) => ({
    id: assignee.value,
    label: assignee.avatar || getInitialsFromFullName(assignee.label),
    fullName: assignee.label,
    color: assignee.avatarCustomColor,
  }));

export const buildAddWorkDto = ({
  data,
  orgId,
  userId,
}: {
  data: AddWorkFormData;
  orgId: string;
  userId: string;
}): AddWorkDto => ({
  name: data.workName || "",
  orgId: null,
  orgRelayId: orgId,
  serviceId: data.workType?.value || "",
  templateId: data.subtaskTemplate?.value || null,
  repeatTypeRecurring: 0,
  dueDate: moment(data.dueDate).utc(),
  startDate: moment(data.startDate).utc(),
  assignedUserIds: data.assignedTo.map((item) => item.value),
  timeBudget: getTimeBudget(data.budgetedTime),
  customerIds: data.contact.map((contact) => contact.value).filter(Boolean),
  workTasks: [],
  roleId: data.role?.value || null,
  assignedByUserId: userId,
});

/** A client just created from the Contact box ("+ Create new contact", NEWW-022/023). */
export const createdContactOption = (
  customerId: string,
  name: string,
  isOrganization: boolean,
): AddWorkContactOption => ({
  value: customerId,
  label: name,
  caption: isOrganization ? "Organization" : "Individual",
  avatar: {
    id: customerId,
    label: getInitialsFromFullName(name),
    fullName: name,
    color: getContactAvatarColor(isOrganization),
    size: "sm",
  },
});

/** Adds the new client to the selected ones (once). */
export const withCreatedContact = (
  selected: AddWorkContactOption[],
  created: AddWorkContactOption,
): AddWorkContactOption[] =>
  selected.some((item) => item.value === created.value)
    ? selected
    : [...selected, created];
